import React, { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ListChecks, Play, Cog } from "lucide-react";

const STATUS_LABELS = {
  queued: { label: "En cola", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  processed: { label: "Procesado", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelado", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  failed: { label: "Fallido", cls: "bg-red-50 text-red-700 border-red-200" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export default function Queue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingAll, setProcessingAll] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/queue");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const processOne = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/queue/${id}/process`);
      toast.success(`Póliza emitida: ${data.policy_id}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusyId(null); }
  };

  const processAll = async () => {
    setProcessingAll(true);
    try {
      const { data } = await api.post("/admin/queue/process-all");
      toast.success(`Procesadas: ${data.processed} · Errores: ${data.errors}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setProcessingAll(false); }
  };

  const queuedCount = items.filter((i) => i.status === "queued").length;

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
            <ListChecks className="w-6 h-6" /> Cola de pólizas pendientes
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Solicitudes recibidas con el adaptador en modo <span className="font-mono font-semibold">queue</span>. Procésalas manualmente para emitir la póliza simulada.
          </p>
        </div>
        <Button
          onClick={processAll}
          disabled={processingAll || queuedCount === 0}
          data-testid="process-all-queue-button"
          className="h-9 bg-slate-900 hover:bg-slate-800 text-white text-xs"
        >
          <Cog className={`w-3.5 h-3.5 mr-1.5 ${processingAll ? "animate-spin" : ""}`} />
          Procesar cola ({queuedCount})
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">ID cola</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Rental</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Estado</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Póliza asignada</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Creada</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Procesada</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-slate-500 py-8" data-testid="queue-loading">
                  Cargando cola…
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-slate-500 py-8" data-testid="queue-empty">
                  La cola está vacía. Las nuevas pólizas en modo <code className="font-mono">queue</code> aparecerán aquí.
                </TableCell>
              </TableRow>
            )}
            {!loading && items.map((q) => {
              const s = STATUS_LABELS[q.status] || STATUS_LABELS.queued;
              return (
                <TableRow key={q.id} data-testid={`queue-row-${q.id}`}>
                  <TableCell className="font-mono text-xs">{q.id}</TableCell>
                  <TableCell className="font-mono text-xs">{q.external_rental_ref}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.cls}`}>
                      {s.label}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-700">{q.assigned_policy_id || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-700">{fmtDate(q.created_at)}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-700">{fmtDate(q.processed_at)}</TableCell>
                  <TableCell className="text-right">
                    {q.status === "queued" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px]"
                        onClick={() => processOne(q.id)}
                        disabled={busyId === q.id}
                        data-testid={`process-queue-item-${q.id}`}
                      >
                        <Play className="w-3 h-3 mr-1" /> Procesar
                      </Button>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Layout>
  );
}
